import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: isin,action_type,ex_date,ratio,amount,currency_code[,notes]
// action_type: dividend | split | merger | spinoff | other
export default function CorporateActionsUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase.from("policy_corporate_actions" as any).select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const num = (v: string) => v && v.trim() ? Number(v) : null;

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const data = lines[0].toLowerCase().includes("isin") ? lines.slice(1) : lines;

      const rows = data.map(l => {
        const [isin, action_type, ex_date, ratioRaw, amountRaw, ccy, ...rest] = l.split(",").map(s => s.trim());
        if (!isin || !action_type || !ex_date) throw new Error(`Bad row: ${l}`);
        return {
          isin: isin.toUpperCase(),
          action_type,
          ex_date,
          ratio: num(ratioRaw),
          amount: num(amountRaw),
          currency_code: ccy ? ccy.toUpperCase() : null,
          notes: rest.join(",") || null,
        };
      });

      const chunkSize = 500;
      let total = 0;
      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const { error } = await supabase
          .from("policy_corporate_actions" as any)
          .upsert(chunk, { onConflict: "isin,action_type,ex_date" });
        if (error) throw error;
        total += chunk.length;
      }
      toast({ title: "Upload complete", description: `${total} corporate actions upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Corporate Actions</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Per-ISIN dividends, splits, mergers, and spinoffs. Used to adjust holding return calculations against benchmark.</p>
          <p><strong>CSV:</strong> <code>isin,action_type,ex_date,ratio,amount,currency_code[,notes]</code></p>
          <p><strong>action_type:</strong> <code>dividend | split | merger | spinoff | other</code>. Use <code>amount</code> for dividends, <code>ratio</code> for splits.</p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">Action rows:</span>{" "}
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"isin,action_type,ex_date,ratio,amount,currency_code,notes\nGB00B7VVMZ49,dividend,2026-04-30,,0.42,GBP,Q1 distribution"}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
